/**
 * Contract Service
 * Handles contract-related API calls
 */
import BaseApiService from '../base/BaseApiService';
import type {
  Contract,
  ContractFilters,
  ContractsResponse,
  CreateContractRequest,
  UpdateContractRequest
} from '@app-types';

class ContractService extends BaseApiService {
  /**
   * Get contracts list with optional filters
   */
  async getContracts(filters: ContractFilters = {}): Promise<ContractsResponse> {
    const params = this.buildQueryParams(filters);
    return this.get<ContractsResponse>(`/contracts?${params}`);
  }

  /**
   * Get all contracts matching the given filters, ignoring pagination — used for
   * full-dataset export.
   */
  async getContractsForExport(
    filters: Omit<ContractFilters, 'limit' | 'offset'> = {}
  ): Promise<{ total: number; data: Contract[] }> {
    const params = this.buildQueryParams(filters);
    return this.get<{ total: number; data: Contract[] }>(`/contracts/export?${params}`);
  }

  /**
   * Get a single contract by ID
   */
  async getContract(id: number): Promise<Contract> {
    return this.get<Contract>(`/contracts/${id}`);
  }

  /**
   * Get available contract types
   */
  async getContractTypes(): Promise<string[]> {
    return this.get<string[]>('/contracts/contract-types');
  }

  /**
   * Create a new contract
   */
  async createContract(data: CreateContractRequest): Promise<{ message: string; contract: Contract }> {
    return this.post<{ message: string; contract: Contract }>('/contracts', data);
  }

  /**
   * Update an existing contract
   */
  async updateContract(id: number, data: UpdateContractRequest): Promise<Contract> {
    return this.put<Contract>(`/contracts/${id}`, data);
  }

  /**
   * Reassociate a contract with a different supplier
   */
  async reassociateContractSupplier(
    contractId: number,
    supplierId: number
  ): Promise<{
    message: string;
    contract: {
      id: number;
      contract_number: string;
      old_supplier: { id: number; name: string };
      new_supplier: { id: number; name: string };
    };
  }> {
    return this.post<{
      message: string;
      contract: {
        id: number;
        contract_number: string;
        old_supplier: { id: number; name: string };
        new_supplier: { id: number; name: string };
      };
    }>(`/contracts/${contractId}/reassociate-supplier`, { supplier_id: supplierId });
  }
}

export const contractService = new ContractService();
export default contractService;
